import type { PreviewLayer } from '@/stores/preview';

export type PreviewStartupSource =
  | 'sandbox_ensure' // 确保沙盒可用
  | 'sandbox_refresh' // 刷新沙盒预览
  | 'iframe_load'; // 加载 iframe

export type PreviewStartupFailureReason =
  | 'runtime_ready_timeout' // Runtime 连接超时
  | 'app_content_ready_timeout' // 页面内容就绪超时
  | 'sandbox_ensure_timeout' // 沙盒准备超时
  | 'sandbox_unavailable' // 沙盒不可用
  | 'sandbox_ensure_failed' // 沙盒准备失败
  | 'no_available_preview' // 无可用预览
  | 'blank_screen_error_check' // ErrorCheck 检测到白屏
  | 'blank_screen_post_generation_check'; // 生成后检测到白屏

export type PreviewStartupFailureStage =
  | 'sandbox_creation' // Sandbox 创建或准备失败
  | 'bridge' // iframe 与父页面之间的 Bridge 建联失败
  | 'runtime' // Bridge 已连接，但 Sandbox Runtime 未就绪
  | 'page_render' // Runtime 已就绪，但页面内容未完成渲染
  | 'blank_screen_check' // 白屏检查
  | 'preview_source'; // 没有可展示的 live 或 stable 预览来源

export interface PreviewStartupContext extends Record<string, unknown> {
  preview_route?: string;
  target_layer?: PreviewLayer;
  active_preview_source?: string;
  displayed_iframe_type?: PreviewLayer | 'none';
  preview_problem?: string;
  live_preview_status?: string;
  sandbox_status?: string;
  runtime_connection_status?: string;
  bridge_status?: string;
  content_ready?: boolean;
  iframe_ready?: boolean;
  has_sandbox_url?: boolean;
  has_stable_url?: boolean;
  plugin_load_status?: string;
}

export interface PreviewStartupTrackerOptions {
  collectEvent: (eventName: 'sandbox_preview_startup', payload: Record<string, unknown>) => void;
  now?: () => number;
  createId?: () => string;
  getContext?: () => PreviewStartupContext;
}

interface PreviewLongLoadingExposure {
  exposureId: string;
  visibleDurationMs: number;
  iframeLoadId?: string;
}

export interface PreviewStartupTracker {
  start: (source: PreviewStartupSource) => string;
  attachIframeLoad: (iframeLoadId: string, startedAt: number | null, source: PreviewStartupSource) => string;
  succeed: () => void;
  fail: (reasonCode: PreviewStartupFailureReason) => void;
  trackErrorPageViewed: () => void;
  trackLongLoadingExposure: (exposure: PreviewLongLoadingExposure) => void;
  reset: () => void;
}

interface PreviewAttempt {
  attemptId: string;
  source: PreviewStartupSource;
  startedAt: number;
  iframeLoadId?: string;
  terminal: 'success' | 'failed' | 'cancelled' | null;
  failureReason?: PreviewStartupFailureReason;
  failureStage?: PreviewStartupFailureStage;
  errorPageTracked: boolean;
}

function defaultNow() {
  return typeof performance !== 'undefined' ? performance.now() : Date.now();
}

function defaultCreateId() {
  if (typeof crypto !== 'undefined' && typeof crypto.randomUUID === 'function') {
    return crypto.randomUUID();
  }
  return `preview-${Date.now()}-${Math.random().toString(36).slice(2)}`;
}

// 没有明确失败事件时，根据预览状态兜底归因。
function inferFailureReason(context: PreviewStartupContext): PreviewStartupFailureReason {
  switch (context.preview_problem) {
    case 'client-unavailable':
      return 'runtime_ready_timeout';
    case 'blank-screen':
      return 'app_content_ready_timeout';
    case 'sandbox-unavailable':
      return 'sandbox_unavailable';
    default:
      return 'sandbox_ensure_failed';
  }
}

function inferFailureStage(
  reasonCode: PreviewStartupFailureReason,
  context: PreviewStartupContext,
): PreviewStartupFailureStage {
  switch (reasonCode) {
    case 'runtime_ready_timeout':
      // Runtime 超时前 Bridge 尚未连上时，失败发生在 Bridge 建联阶段。
      return context.bridge_status === 'connected' ? 'runtime' : 'bridge';
    case 'app_content_ready_timeout':
      return 'page_render';
    case 'blank_screen_error_check':
    case 'blank_screen_post_generation_check':
      return 'blank_screen_check';
    case 'no_available_preview':
      return 'preview_source';
    case 'sandbox_ensure_timeout':
    case 'sandbox_unavailable':
    case 'sandbox_ensure_failed':
    default:
      return 'sandbox_creation';
  }
}

export function createPreviewStartupTracker({
  collectEvent,
  now = defaultNow,
  createId = defaultCreateId,
  getContext = () => ({}),
}: PreviewStartupTrackerOptions): PreviewStartupTracker {
  let activeAttempt: PreviewAttempt | null = null;

  const getSafeContext = (): PreviewStartupContext => {
    try {
      return getContext();
    } catch {
      return {};
    }
  };

  const trackEvent = (buildEventPayload: () => Record<string, unknown>) => {
    try {
      collectEvent('sandbox_preview_startup', {
        ...getSafeContext(),
        ...buildEventPayload(),
      });
    } catch (error) {
      console.warn('[PreviewTelemetry] Failed to collect startup telemetry', error);
    }
  };

  const trackAttempt = (
    attempt: PreviewAttempt,
    buildEventPayload: () => Record<string, unknown>,
  ) => {
    trackEvent(() => ({
      schema_version: 1,
      attempt_id: attempt.attemptId,
      ...(attempt.iframeLoadId ? { iframe_load_id: attempt.iframeLoadId } : {}),
      startup_source: attempt.source,
      ...(attempt.failureStage ? { failure_stage: attempt.failureStage } : {}),
      ...buildEventPayload(),
    }));
  };

  const createAttempt = (
    source: PreviewStartupSource,
    iframeLoadId?: string,
    iframeStartedAt?: number | null,
  ) => {
    const createdAt = now();
    const attempt: PreviewAttempt = {
      attemptId: createId(),
      source,
      startedAt:
        iframeStartedAt !== null && iframeStartedAt !== undefined && Number.isFinite(iframeStartedAt)
          ? Math.min(createdAt, iframeStartedAt)
          : createdAt,
      iframeLoadId,
      terminal: null,
      errorPageTracked: false,
    };
    activeAttempt = attempt;
    trackAttempt(attempt, () => ({ status: 'started' }));
    return attempt;
  };

  return {
    start(source) {
      if (activeAttempt && !activeAttempt.terminal) {
        return activeAttempt.attemptId;
      }
      return createAttempt(source).attemptId;
    },
    attachIframeLoad(iframeLoadId, startedAt, source) {
      if (activeAttempt && !activeAttempt.terminal) {
        if (!activeAttempt.iframeLoadId) {
          activeAttempt.iframeLoadId = iframeLoadId;
          return activeAttempt.attemptId;
        }
        if (activeAttempt.iframeLoadId === iframeLoadId) {
          return activeAttempt.attemptId;
        }
        activeAttempt.terminal = 'cancelled';
        trackAttempt(activeAttempt, () => ({
          status: 'cancelled',
          duration_ms: Math.max(0, Math.round(now() - activeAttempt.startedAt)),
          reason_code: 'iframe_load_replaced',
        }));
      }
      const attempt = createAttempt(source, iframeLoadId, startedAt);
      return attempt.attemptId;
    },
    succeed() {
      const attempt = activeAttempt;
      if (!attempt || attempt.terminal) {
        return;
      }
      attempt.terminal = 'success';
      trackAttempt(attempt, () => ({
        status: 'success',
        duration_ms: Math.max(0, Math.round(now() - attempt.startedAt)),
      }));
    },
    fail(reasonCode) {
      const attempt = activeAttempt;
      if (!attempt) {
        return;
      }
      if (attempt.terminal === 'failed' || attempt.terminal === 'cancelled') {
        return;
      }

      const context = getSafeContext();
      const resolvedFailureStage = inferFailureStage(reasonCode, context);
      attempt.terminal = 'failed';
      attempt.failureReason = reasonCode;
      attempt.failureStage = resolvedFailureStage;
      trackAttempt(attempt, () => ({
        status: 'failed',
        duration_ms: Math.max(0, Math.round(now() - attempt.startedAt)),
        reason_code: reasonCode,
      }));
    },
    trackErrorPageViewed() {
      const attempt = activeAttempt;
      if (!attempt || attempt.errorPageTracked) {
        return;
      }
      attempt.errorPageTracked = true;
      const context = getSafeContext();
      const reasonCode = attempt.failureReason ?? inferFailureReason(context);
      attempt.failureReason = reasonCode;
      attempt.failureStage ??= inferFailureStage(reasonCode, context);
      trackAttempt(attempt, () => ({
        status: 'error_page_viewed',
        reason_code: reasonCode,
        failure_surface: 'sandbox_loading_page',
        displayed_iframe_type: 'none',
      }));
    },
    trackLongLoadingExposure({ exposureId, visibleDurationMs, iframeLoadId }) {
      trackEvent(() => {
        const latestIframeLoadId = iframeLoadId ?? activeAttempt?.iframeLoadId;
        return {
          schema_version: 2,
          ...(activeAttempt
            ? {
                attempt_id: activeAttempt.attemptId,
                startup_source: activeAttempt.source,
              }
            : {}),
          ...(latestIframeLoadId ? { iframe_load_id: latestIframeLoadId } : {}),
          status: 'abnormal_exposure',
          abnormal_type: 'long_loading',
          exposure_id: exposureId,
          visible_duration_ms: visibleDurationMs,
          displayed_iframe_type: 'none',
        };
      });
    },
    reset() {
      activeAttempt = null;
    },
  };
}
